import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CsrfService } from './csrf.service';
import { isBackendUrl } from './backend-url';

/**
 * Intercepts HTTP 401 responses from the backend and redirects the user to the
 * login page for regular protected endpoints. HTTP 403 represents an
 * authenticated but unauthorized request and is propagated for feature-specific
 * handling.
 * The backend sets the session as an HttpOnly cookie; when it becomes invalid
 * (e.g. expiry, reset), protected endpoints respond with 401. This interceptor
 * ensures the SPA navigates to the login page immediately instead of showing a
 * stuck error state. HTTP 403 responses remain available to the feature that
 * requested them for authorization-specific handling.
 *
 * ## Skipped endpoints
 *
 * **`/api/user/current`** – Used by AuthService and authGuard to query the
 * authentication state. AuthService handles both 401 and 403 here as an expected
 * unauthenticated state (emits null); the interceptor exclusion is relevant to
 * the 401 case because that is the status that triggers login navigation. If the
 * interceptor intervened here too, it would create a redirect loop for
 * unauthenticated guests.
 *
 * **`/logout`** – The logout flow has its own navigation: on successful logout
 * the backend returns 200 and the caller owns navigation; on error the AuthService
 * handles it deliberately. An additional redirect by this interceptor would
 * override that logic and could produce an unwanted intermediate state (e.g.
 * double redirect or aborted error handling).
 */
export const sessionInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const csrf = inject(CsrfService);

  return next(req).pipe(
    catchError((error: unknown) => {
      const isBackendFailure = isBackendUrl(req.url);
      // Any backend 401/403 can indicate an expired/replaced session or CSRF state.
      // Clear broadly so the next mutation obtains a token bound to the current session.
      if (
        isBackendFailure &&
        error instanceof HttpErrorResponse &&
        (error.status === 401 || error.status === 403)
      ) {
        csrf.clear();
      }

      if (isBackendFailure && error instanceof HttpErrorResponse && error.status === 401) {
        // `/api/user/current`: authGuard checks the session status here – 401 is
        // expected and handled by AuthService (no redirect); AuthService also
        // handles 403 as unauthenticated.
        // `/logout`: Logout has its own navigation; an additional redirect
        // would interfere with the logout logic.
        const shouldSkip = isAuthStatusRequest(req.url) || isLogoutRequest(req.url);
        if (!shouldSkip) {
          router.navigate(['/login']);
        }
      }

      return throwError(() => error);
    })
  );
};

function isAuthStatusRequest(url: string): boolean {
  return getPathname(url).endsWith('/user/current');
}

function isLogoutRequest(url: string): boolean {
  return getPathname(url).endsWith('/logout');
}

function getPathname(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
}
